import { Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { Validators, AbstractControl, ValidationErrors, ValidatorFn, UntypedFormGroup, UntypedFormBuilder } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';

import { faEdit, faFileCheck, faFileXmark, faSpinnerThird, faTimes } from '@fortawesome/pro-solid-svg-icons';
import { Subscription } from 'rxjs';
import { SalesForm } from '../shared/models/sales-form.model';
import { environment } from '../../environments/environment';

import { CaptchaService } from '../shared/services/captcha.service';
import { FormService } from '../shared/services/form.service';
import { SalesService } from '../shared/services/sales.service';
import { UploadService } from '../shared/services/upload.service';
import { Brand } from "../shared/models/brand.model";

/**
 * Main component for the app, showing a form for completion.
 *
 * @export
 * @class FormComponent
 * @implements {OnInit}
 * @implements {OnDestroy}
 */
@Component({
    selector: 'app-form',
    templateUrl: './form.component.html',
    standalone: false
})
class FormComponent implements OnInit, OnDestroy {
  /**
   * Indicates if the form is currently loading or not.
   *
   * @memberof FormComponent
   */
  public isLoading = true;

  /**
   * Indicates if an attachment is being uploaded or not.
   *
   * @memberof FormComponent
   */
  public isUploading = false;

  /**
   * Indicates if the request is valid or not.
   *
   * @memberof FormComponent
   */
  public isValidRequest = false;

  /**
   * Whether currently submitting or not.
   *
   * @memberof FormComponent
   */
  public isSubmitting = false;

  /**
   *
   *  Whether this record has already been submitted or not.
   *
   * @memberof FormComponent
   */
  public hasAlreadyBeenSubmitted = false;

  /**
   * Whether this record has been submitted or not.
   *
   * @memberof FormComponent
   */
  public hasSubmitted = false;

  /**
   * Whether attachment upload is allowed or not.
   *
   * @memberof FormComponent
   */
  public allowUpload = false;

  /**
   * Indicates if the submission was successful or not.
   *
   * @type {(boolean | null)}
   * @memberof FormComponent
   */
  public wasSubmitSuccessful: boolean | null = null;

  /**
   * Days the user has left to edit this record.
   *
   * @type {number}
   * @memberof FormComponent
   */
  public daysLeftToEdit?: number;

  /**
   * Indicates is an attachment is already supplied.
   *
   * @memberof FormComponent
   */
  public attachmentAlreadySupplied = false;

  /**
   * Icon for showing a loading indicator.
   *
   * @memberof FormComponent
   */
  public faSpinnerThird = faSpinnerThird;

  /**
   * Icon for showing the ability to edit.
   *
   * @memberof FormComponent
   */
  public faEdit = faEdit;

  /**
   * Icon for showing a file which has been accepted.
   *
   * @memberof FormComponent
   */
  public faFileCheck = faFileCheck;

  /**
   * Icon for showing a file which will be deleted.
   *
   * @memberof FormComponent
   */
  public faFileXmark = faFileXmark;

  /**
   * Icon for closing/canceling.
   *
   * @memberof FormComponent
   */
  public faTimes = faTimes;
  public allowedExtension = ['PDF', 'TXT', 'XLS', 'DOC', 'RTF', 'BMP', 'GIF', 'JPG', 'PNG', 'TIF', 'JPEG', 'ZIP', 'XLSX', 'DOCX', 'MSG'];

  public isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

  public captchaEnabled = true;

  public isCaptchaLoading = false;

  public captchaErrorMessage: string | null = null;

  private captchaWidgetId: number | null = null;

  private captchaRenderAttempts = 0;

  private isSubmitRequestInFlight = false;

  public brand?: Brand;

  public showDebugInfo = environment.showDebugInfo;

  public debugInfo: any = null;

  /**
   * ID of the record loaded in.
   *
   * @private
   * @type {(string | null)}
   * @memberof FormComponent
   */
  private id: string | null = null;

  /**
   * Subscription to watch for loaded result.
   *
   * @private
   * @type {(Subscription | null)}
   * @memberof FormComponent
   */
  private loadSubscription: Subscription | null = null;

  /**
   * Subscription to watch for the submission to complete.
   *
   * @private
   * @type {(Subscription | undefined)}
   * @memberof FormComponent
   */
  private submitSubscription: Subscription | undefined = undefined;

  /**
   * Subscription to watch for changes to the Report Type field/control.
   *
   * @private
   * @type {(Subscription | undefined)}
   * @memberof FormComponent
   */
  private reportTypeSubscription: Subscription | undefined = undefined;

  /**
   * Check for if the attachment field was dirty prior to removal of the attachment.
   *
   * @private
   * @memberof FormComponent
   */
  private formWasDirtyPriorToAttachmentRemoval = false;

  /**
   * Previous value of the attachment field.
   *
   * @private
   * @memberof FormComponent
   */
  private previousAttachmentValue = null;

  /**
   * Validate a upload file.
   * @returns Whether valid or not.
   */
  ValidateFile(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;

      if (!value) {
        return null;
      }

      return this.allowedExtension.indexOf(value.split('.').pop().toUpperCase()) === -1 ? { invalidFile: true } : null;
    }
  }

  /**
   * Form payload.
   *
   * @type {FormGroup}
   * @memberof HomeComponent
   */
  form: UntypedFormGroup = this.fb.group({
    buildingNameAndBuildingID: [''],
    tenantTradingNameAndLeaseID: [''],
    suiteIDAndSuiteNumber: [''],
    salesContactEmail: [''],
    salesPeriod: [''],
    reportType: ['', Validators.compose([
      Validators.required
    ])],
    salesFigure: ['', Validators.compose([
      Validators.required,
      Validators.pattern(/^[0-9]+(\.[0-9]{1,2})?$/)
    ])],
    attachment: ['', Validators.compose([
      this.ValidateFile()
    ])],
    comments: ['', Validators.compose([
      Validators.maxLength(300)
    ])]
  }, { updateOn: 'change' });

  /**
   * Form control quick access.
   *
   * @readonly
   * @memberof HomeComponent
   */
  get f() {
    return this.form?.controls;
  }

  /**
   * Creates an instance of FormComponent.
   *
   * @param {ActivatedRoute} route Angular active route service.
   * @param {FormBuilder} fb Angular form service.
   * @param {FormService} formService Form validation service.
   * @param {SalesService} salesService Sales service.
   * @param {UploadService} uploadService Upload service.
   * @memberof HomeComponent
   */
  constructor(
    private route: ActivatedRoute,
    private fb: UntypedFormBuilder,
    private formService: FormService,
    private salesService: SalesService,
    private uploadService: UploadService,
    private captchaService: CaptchaService,
    private ngZone: NgZone
  ) { }

  /**
   * On component initiated.
   */
  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.id = params.id;
      this.load();
    });

    if (!this.isLocalhost) {
      this.isCaptchaLoading = true;

      this.captchaService.getConfig().subscribe({
        next: config => {
          this.captchaEnabled = config.enabled;
          if (this.captchaEnabled) {
            this.loadHCaptcha();
          } else {
            this.isCaptchaLoading = false;
          }
        },
        error: () => {
          this.captchaEnabled = true;
          this.loadHCaptcha();
        }
      });
    }

    this.reportTypeSubscription = this.form.get('reportType')?.valueChanges.subscribe((val: string) => {
      if (val === 'CER') {
        this.form.controls['attachment'].setValidators(Validators.compose([Validators.required, this.ValidateFile()]));
      } else {
        this.form.controls['attachment'].setValidators(Validators.compose([this.ValidateFile()]));
      }

      this.form.controls['attachment'].updateValueAndValidity();
    });
  }

  /**
   * On component destroyed.
   */
  ngOnDestroy(): void {
    if (this.loadSubscription) {
      this.loadSubscription.unsubscribe();
    }

    if (this.submitSubscription) {
      this.submitSubscription.unsubscribe();
    }

    if (this.reportTypeSubscription) {
      this.reportTypeSubscription.unsubscribe();
    }
  }

  /**
   * Load the form, and the overall screen/page.
   */
  load() {
    this.isLoading = true;
    this.debugInfo = null;

    if (!this.id) {
      this.isLoading = false;
      this.isValidRequest = false;
      return;
    }

    this.loadSubscription = this.salesService.get(this.id).subscribe(
      (res: any) => {
        const record = res?.form ?? res;

        this.isLoading = false;
        this.isValidRequest = true;
        this.hasSubmitted = false;
        this.wasSubmitSuccessful = null;

        this.daysLeftToEdit = record.daysLeftToEdit;
        this.attachmentAlreadySupplied = !!record.attachment;

        this.brand = res?.brand;

        this.form.patchValue(record);
        this.form.markAsPristine();
        this.checkIfAlreadySubmitted(record);
      },
      (err: any) => {
        this.isLoading = false;
        this.isValidRequest = false;
        this.debugInfo = environment.showDebugInfo ? (err?.error ?? null) : null;
      }
    );
  }

  /**
   * Check if this record has already been submitted or not.
   * @param record The sales record.
   */
  checkIfAlreadySubmitted(record: SalesForm) {
    if (record.reportType !== null || record.salesFigure !== null || record.attachment !== null) {
      this.hasAlreadyBeenSubmitted = true;
    }
  }

  /**
   * Handle file drop/selection.
   * @param event The triggering event.
   * @returns void
   */
  onFileChange(event: any) {
    if (event.target.files && event.target.files.length) {
      const [file] = event.target.files;
      const fileName = file.name;

      if (!fileName || !this.id) {
        return;
      }


      this.isUploading = true;

      this.uploadService.getSas(this.id, fileName).subscribe(async uploadConfig => {
        const successful = await this.uploadService.uploadFile(uploadConfig, file);

        if (successful) {
          this.form.controls['attachment'].setValue(uploadConfig.path);
          this.form.markAsDirty();
          this.allowUpload = false;
          this.isUploading = false;
        }
      });
    }
  }

  onFormSubmit(): void {
    if (this.isSubmitting || this.wasSubmitSuccessful) {
      return;
    }

    this.captchaErrorMessage = null;

    if (this.isLocalhost || !this.captchaEnabled) {
      this.submit();
    } else {
      this.triggerCaptcha();
    }
  }

  triggerCaptcha(): void {
    if (this.form.invalid) {
      return this.formService.validate(this.form);
    }

    const hcaptcha = (window as any).hcaptcha;

    if (!hcaptcha || this.captchaWidgetId === null) {
      this.isCaptchaLoading = false;
      this.onCaptchaError('Captcha could not be loaded. Please refresh the page and try again.');
      return;
    }

    this.isSubmitting = true;

    try {
      hcaptcha.execute(this.captchaWidgetId);
    } catch {
      this.onCaptchaError('Captcha could not be started. Please refresh the page and try again.');
    }
  }

  private loadHCaptcha(): void {
    (window as any).hcaptchaLoaded = () => this.ngZone.run(() => this.renderCaptcha());

    if (document.querySelector('script[data-hcaptcha-script="true"]')) {
      this.renderCaptcha();
      return;
    }

    const script = document.createElement('script');
    script.setAttribute('data-hcaptcha-script', 'true');
    script.src = 'https://hcaptcha.com/1/api.js?onload=hcaptchaLoaded&render=explicit';
    script.async = true;
    script.defer = true;
    script.onerror = () => this.ngZone.run(() =>
      this.onCaptchaError('Captcha could not be loaded. Please refresh the page and try again.')
    );
    document.head.appendChild(script);
  }

  private renderCaptcha(): void {
    if (this.captchaWidgetId !== null) {
      return;
    }

    const container = document.getElementById('captcha-container');
    const hcaptcha = (window as any).hcaptcha;

    if (!container || !hcaptcha) {
      if (this.captchaRenderAttempts < 10) {
        this.captchaRenderAttempts++;
        window.setTimeout(() => this.ngZone.run(() => this.renderCaptcha()), 250);
      } else {
        this.onCaptchaError('Captcha could not be loaded. Please refresh the page and try again.');
      }

      return;
    }

    try {
      this.captchaWidgetId = hcaptcha.render(container, {
        sitekey: environment.hCaptchaSiteKey,
        size: 'invisible',
        callback: (token: string) => this.ngZone.run(() => this.onVerify(token)),
        'error-callback': () => this.ngZone.run(() => this.onCaptchaError()),
        'expired-callback': () => this.ngZone.run(() => this.onCaptchaExpired()),
      });
      this.captchaErrorMessage = null;
      this.isCaptchaLoading = false;
    } catch {
      this.onCaptchaError('Captcha could not be loaded. Please refresh the page and try again.');
    }
  }

  private onVerify(token: string): void {
    this.captchaErrorMessage = null;

    this.captchaService.verify(token).subscribe({
      next: (result) => {
        if (result.success) {
          this.submit();
        } else {
          this.isSubmitting = false;
          this.captchaErrorMessage = 'Captcha verification failed. Please try again.';
          this.resetCaptcha();
        }
      },
      error: () => {
        this.isSubmitting = false;
        this.captchaErrorMessage = 'Captcha verification failed. Please try again.';
        this.resetCaptcha();
      }
    });
  }

  private onCaptchaError(message = 'Captcha failed. Please try again.'): void {
    this.isSubmitting = false;
    this.isCaptchaLoading = false;
    this.captchaErrorMessage = message;
    this.resetCaptcha();
  }

  private onCaptchaExpired(): void {
    this.isSubmitting = false;
    this.isCaptchaLoading = false;
    this.captchaErrorMessage = 'Captcha expired. Please try again.';
    this.resetCaptcha();
  }

  private resetCaptcha(): void {
    if (this.captchaWidgetId === null) {
      return;
    }

    try {
      (window as any).hcaptcha?.reset?.(this.captchaWidgetId);
    } catch {
      this.captchaWidgetId = null;
      this.renderCaptcha();
    }
  }

  /**
   * Submit the form.
   * @returns void
   */
  submit() {
    if (this.isSubmitRequestInFlight || this.wasSubmitSuccessful) {
      return;
    }

    if (this.form.invalid) {
      this.isSubmitting = false;
      return this.formService.validate(this.form);
    }


    if (!this.id) {
      this.isSubmitting = false;
      return;
    }

    this.isSubmitting = true;
    this.isSubmitRequestInFlight = true;
    this.wasSubmitSuccessful = null;

    this.submitSubscription = this.salesService
      .submit(this.id, this.form.value)
      .subscribe({
        next: (wasSuccessful) => {
          this.ngZone.run(() => {
            const submitResponse = wasSuccessful as unknown;
            const submitSucceeded =
              submitResponse === true ||
              submitResponse === 'true' ||
              (submitResponse as { success?: boolean })?.success === true;

            this.hasSubmitted = true;
            this.wasSubmitSuccessful = submitSucceeded;
            this.allowUpload = false;
            this.isUploading = false;
            this.isSubmitting = false;
            this.isSubmitRequestInFlight = false;

            if (submitSucceeded) {
              this.form.markAsPristine();
            }
          });
        },
        error: () => {
          this.ngZone.run(() => {
            this.hasSubmitted = true;
            this.wasSubmitSuccessful = false;
            this.isSubmitting = false;
            this.isSubmitRequestInFlight = false;
            this.resetCaptcha();
          });
        }
      });
  }

  /**
   * Toggle the existing attachment messaging.
   */
  toggleExistingAttachment() {
    this.allowUpload = !this.allowUpload;

    if (this.allowUpload) {
      this.formWasDirtyPriorToAttachmentRemoval = this.form.dirty;
      this.previousAttachmentValue = this.form.controls['attachment'].value;
      this.form.controls['attachment'].setValue(null);
      this.form.markAsDirty();
    } else {
      this.form.controls['attachment'].setValue(this.previousAttachmentValue);

      if (!this.formWasDirtyPriorToAttachmentRemoval) {
        this.form.markAsPristine();
      }
    }
  }
}

export default FormComponent
